import { useState } from 'react';
import { listBotAudiences } from '../api/endpoints';
import { getSiteIntegrations, widgetInboxId } from '../api/siteIntegrations';
import type { ManagedInbox } from '../api/types';
import { BUTTON_SNIPPET, IDENTITY_SNIPPET, chatSnippet } from '../lib/chatSnippet';
import { useLoad } from '../lib/useLoad';
import { Note } from './ErrorNote';

/**
 * The embed snippet — the one thing a website channel exists to hand out. The
 * CONSOLE origin serves widget.js, so the snippet is composed here from
 * window.location.origin rather than serialized by the service, which does not
 * reliably know this origin.
 *
 * It lives in two places on purpose: here on the inbox, where the channel is
 * configured, and as the first step of the Chatbot screen's Install section,
 * where someone setting up an assistant for the first time is looking for
 * exactly this and has no reason to guess that "inbox" is where a website
 * snippet hides. The text itself is `lib/chatSnippet.ts`, shared by both.
 *
 * The audience picker writes ONE line of that snippet. It exists because the
 * page is where the audience is decided — the same install can sit on a public
 * site and on a chauffeur portal, and the only thing that tells the assistant
 * which it is on is the page it was pasted into. The default is chosen by
 * writing no line at all, which is exactly the snippet every tenant already has.
 *
 * The inbox the dispatch dashboard's Website chat switch points at says so:
 * the booking-widget pages carry it without anybody pasting this, so the
 * snippet is only for their other pages and sites. Read fail-soft — no answer
 * is no note, and the card reads as it always has.
 */
export function WebsiteEmbed({
  inbox,
  title = 'Add it to your website',
  sub,
}: {
  inbox: ManagedInbox & { channelType: 'website' };
  title?: string;
  sub?: string;
}) {
  const [copied, setCopied] = useState(false);
  const [audience, setAudience] = useState<string | null>(null);
  // Served, never listed here: the audiences a snippet may name are the
  // service's, and a console offering one it does not know would hand out a
  // page the assistant answers as a customer anyway.
  const audiences = useLoad(listBotAudiences);
  const others = (audiences.data ?? []).filter((row) => !row.isDefault);
  const integrations = useLoad(getSiteIntegrations);
  const onWidgetPages = widgetInboxId(integrations.data) === inbox.id;
  const chosen = others.find((row) => row.id === audience) ?? null;
  const snippet = chatSnippet({
    websiteToken: inbox.channel.websiteToken,
    consoleOrigin: window.location.origin,
    audience: chosen?.id ?? null,
  });

  const copy = async () => {
    // Clipboard needs a secure context; the snippet is on screen either way.
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const choose = (next: string | null) => {
    setAudience(next);
    // The snippet on screen is no longer the one in their clipboard.
    setCopied(false);
  };

  return (
    <section className="card card-pad manage-card">
      <h2 className="manage-card-title">{title}</h2>
      <p className="manage-card-sub">
        {sub ?? (
          <>
            Paste this just before the closing <code>&lt;/body&gt;</code> tag on every page that
            should show the chat bubble. Appearance changes apply on the visitor's next page load —
            the snippet never needs re-pasting.
          </>
        )}
      </p>
      {onWidgetPages && (
        <Note tone="info">
          Shown on your reservation widget pages — switched on in the dispatch dashboard under{' '}
          <b>My Website → Widgets</b>.
        </Note>
      )}
      {others.length > 0 && (
        <div className="embed-audience">
          <span className="field-label">Who is this page for?</span>
          <div className="embed-audience-switch">
            <button type="button" className={chosen ? '' : 'is-on'} onClick={() => choose(null)}>
              Customers
            </button>
            {others.map((row) => (
              <button
                key={row.id}
                type="button"
                className={chosen?.id === row.id ? 'is-on' : ''}
                onClick={() => choose(row.id)}
              >
                {row.label}
              </button>
            ))}
          </div>
          <span className="field-hint">
            {chosen
              ? `${chosen.describe} The assistant answers as your assistant for ${chosen.label.toLowerCase()} on any page carrying this snippet.`
              : 'The default. Paste this on your public site — no audience line needed.'}
          </span>
        </div>
      )}
      <pre className="embed-snippet">
        <code>{snippet}</code>
      </pre>
      <div className="manage-card-actions manage-card-actions-start">
        <button className="btn btn-primary" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy snippet'}
        </button>
      </div>
      <p className="manage-card-sub">
        Optional — open it from your own button. Works with the bubble as well as instead of it
        (Chatbot → Chat widget), and needs no change to the snippet above.
      </p>
      <pre className="embed-snippet">
        <code>{BUTTON_SNIPPET}</code>
      </pre>
      <p className="manage-card-sub">
        Optional — recognise signed-in customers. Pass their sign-in token (a LimoSphere customer
        sign-in, or your site's own Firebase sign-in) and the chatbot can look up their reservations
        without the verification-code exchange; pass <code>null</code> when they sign out, so the
        next person on that browser starts a fresh chat instead of reading theirs.
        {onWidgetPages ? ' Your reservation widget pages do both by themselves.' : null}
      </p>
      <pre className="embed-snippet">
        <code>{IDENTITY_SNIPPET}</code>
      </pre>
    </section>
  );
}
